import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { BottomNav } from "../../components/BottomNav";
import { MemoriaalPanel } from "../../components/MemoriaalPanel";
import { statementOptionLabel } from "../../lib/format";
import type { CardStatement, User } from "../../types";

type ExportFormat = "csv" | "json" | "pdf";

export function AdminExportPage() {
  const [statement, setStatement] = useState("");
  const [statements, setStatements] = useState<CardStatement[]>([]);
  const [format, setFormat] = useState<ExportFormat>("pdf");
  const [status, setStatus] = useState<"matched" | "insufficient">("matched");
  const [userId, setUserId] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStatements() {
    const list = await api.request<CardStatement[]>("/imports/statements");
    setStatements(list);
    return list;
  }

  useEffect(() => {
    api.request<User[]>("/users").then(setUsers).catch(console.error);
    loadStatements()
      .then((list) => {
        if (list.length) setStatement(list[0].key);
      })
      .catch(console.error);
  }, []);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        format,
        status,
      });
      if (statement) qs.set("statement", statement);
      if (userId) qs.set("user_id", userId);
      const blob = await api.download(`/exports?${qs}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipts-export-${statement || "all"}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell admin">
      <header className="topbar">
        <h2 style={{ fontSize: "1.35rem" }}>Export</h2>
      </header>

      <div className="panel stack">
        <p className="muted" style={{ margin: 0 }}>
          Export matched transactions for accounting, per credit card statement: exactly the
          lines of that ING CSV import, over the dates it covers. PDF includes every matched
          receipt scan and original (foreign) currency when present, and never includes
          Boekhoudgemak bank lines. CSV uses semicolon separators and marks each line as card
          or bank. Receipts flagged as insufficient include the admin note and the employee's
          explanation.
        </p>
        <div className="field">
          <label>Credit card statement</label>
          <select value={statement} onChange={(e) => setStatement(e.target.value)}>
            {statements.map((s) => (
              <option key={s.key} value={s.key}>
                {statementOptionLabel(s)}
              </option>
            ))}
            <option value="">All statements</option>
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "matched" | "insufficient")}
          >
            <option value="matched">Matched</option>
            <option value="insufficient">Insufficient — awaiting employee</option>
          </select>
        </div>
        <div className="field">
          <label>Employee</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">All employees</option>
            {users
              .filter((u) => u.role === "employee" || u.role === "admin")
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
          </select>
        </div>
        <div className="field">
          <label>Format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)}>
            <option value="pdf">PDF (with receipt images)</option>
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </select>
        </div>
        {error && <div className="alert">{error}</div>}
        <button className="btn btn-primary" type="button" disabled={busy} onClick={download}>
          {busy ? "Preparing…" : format === "pdf" ? "Download PDF with scans" : "Download matched data"}
        </button>
      </div>

      <MemoriaalPanel
        statement={statement}
        statements={statements}
        onStatementsChanged={loadStatements}
      />

      <BottomNav />
    </div>
  );
}
